<template>
  <div class="dynamic-appointment-flow">
    <!-- Step 1: Phone Number -->
    <div v-if="step === 1">
      <h2>{{ isAcademyEnquiry ? 'Academy Enquiry' : 'Book an Appointment' }}</h2>
      <p v-if="isStaff">Search for a customer by phone number, name, or email.</p>
      <p v-else-if="isAcademyEnquiry">Enter your phone number to enquire about academy courses.</p>
      <p v-else>Enter your phone number to get started.</p>
      <div class="form-group">
        <label for="phone-number">{{ isStaff ? 'Customer Search' : 'Phone Number' }}</label>
        <input
          type="tel"
          id="phone-number"
          v-model="phoneNumber"
          :placeholder="isStaff ? 'Type phone number or name...' : 'e.g. 9876543210'"
          autocomplete="off"
          @keyup.enter="searchCustomer"
          @input="onPhoneInput"
        />
        <small v-if="phoneError" class="error-text">{{ phoneError }}</small>
        <small v-else-if="isStaff && isSearching" class="hint-text">Searching...</small>
      </div>

      <!-- Staff: live suggestions while typing -->
      <div v-if="isStaff && showStaffSuggestions" class="suggestions-panel">
        <p class="suggestions-title">Matching customers</p>
        <ul v-if="searchResults.length" class="search-results">
          <li v-for="customer in searchResults" :key="customer.id" @click="selectCustomer(customer)">
            <div>
              <strong>{{ customer.firstName }} {{ customer.lastName }}</strong>
              <small>{{ customer.phoneNumber }}</small>
              <small v-if="customer.email">{{ customer.email }}</small>
            </div>
            <span class="select-hint">Select →</span>
          </li>
        </ul>
        <p v-else-if="!isSearching && phoneNumber.trim().length >= minSuggestionLength" class="no-results">
          No customers found. Click Continue to create a new profile.
        </p>
        <button
          v-if="phoneNumber.trim().length >= minSuggestionLength"
          type="button"
          class="btn btn-secondary btn-block"
          @click="proceedToCreateFromSearch"
        >
          + Create New Customer
        </button>
      </div>

      <button @click="searchCustomer" class="btn btn-primary" :disabled="isSearching">
        {{ isSearching ? 'Searching...' : 'Continue' }}
      </button>
    </div>

    <!-- Step 2: Select profile (multiple customers on same phone) -->
    <div v-if="step === 2">
      <h2>Select Your Profile</h2>
      <p>We found {{ searchResults.length }} profile(s) for <strong>{{ phoneNumber }}</strong>. Please select yours.</p>
      <ul class="search-results">
        <li v-for="customer in searchResults" :key="customer.id" @click="selectCustomer(customer)">
          <div>
            <strong>{{ customer.firstName }} {{ customer.lastName }}</strong>
            <small v-if="customer.email">{{ customer.email }}</small>
          </div>
          <span class="select-hint">Select →</span>
        </li>
      </ul>
      <div class="new-customer-prompt">
        <p>Not you? Create a new profile with this phone number.</p>
        <button @click="proceedToCreate" class="btn btn-secondary">Create New Profile</button>
        <button @click="goBackToPhone" class="btn btn-link">← Change phone number</button>
      </div>
    </div>

    <!-- Step 3: New customer details -->
    <div v-if="step === 3">
      <h2>{{ isAcademyEnquiry ? 'Your Details' : 'Create Your Profile' }}</h2>
      <p>
        We couldn't find a profile for <strong>{{ phoneNumber }}</strong>.
        {{ isAcademyEnquiry ? 'Please enter your details to submit your enquiry.' : 'Please enter your details.' }}
      </p>
      <CustomerForm
        :is-embedded="true"
        :is-public-booking="!isStaff"
        :customer="{ phoneNumber }"
        @save-success="handleCustomerSave"
        @close="goBackToPhone"
      />
      <button @click="goBackToPhone" class="btn btn-link">← Back to phone number</button>
    </div>

    <!-- Step 4: Service & timing (booking only) -->
    <div v-if="step === 4 && !isAcademyEnquiry">
      <h2>Book Appointment</h2>
      <p class="customer-label">
        Booking for: <strong>{{ selectedCustomer?.firstName }} {{ selectedCustomer?.lastName }}</strong>
        <button @click="changeCustomer" class="btn btn-link">Change</button>
      </p>
      <AppointmentForm
        :is-embedded="true"
        :customer="selectedCustomer"
        :show-price="showPrice"
        :is-staff="isStaff"
        @submit="handleAppointmentSubmit"
        @cancel="goBackToPhone"
      />
    </div>

    <div v-if="isSubmitting" class="submitting-overlay">
      <p>{{ isAcademyEnquiry ? 'Submitting your enquiry...' : 'Booking your appointment...' }}</p>
    </div>

    <p v-if="flowError" class="error-text flow-error">{{ flowError }}</p>
  </div>
</template>

<script lang="ts">
import { computed, ComputedRef, defineComponent, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import { useCustomerStore } from '@/store/customer.store';
import { useAppointmentStore } from '@/store/appointment.store';
import { useAuthStore } from '@/store/auth.store';
import { hasAdminPortalAccess } from '@/constants/roles';
import { Customer } from '@/types/customer.types';
import CustomerForm from './CustomerForm.vue';
import AppointmentForm from './AppointmentForm.vue';
import { getApiErrorMessage } from '@/utils/apiError';

function debounce(fn: Function, delay: number) {
  let timeout: ReturnType<typeof setTimeout>;
  return (...args: any[]) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => fn(...args), delay);
  };
}

function normalizePhone(phone: string): string {
  return phone.replace(/\s+/g, '').trim();
}

export default defineComponent({
  name: 'DynamicAppointmentFlow',
  components: {
    CustomerForm,
    AppointmentForm,
  },
  props: {
    showPrice: {
      type: Boolean,
      default: true,
    },
    flowType: {
      type: String as () => 'booking' | 'academy-enquiry',
      default: 'booking',
    },
    /** Prefill phone and auto-lookup (e.g. after public spin). */
    initialPhoneNumber: {
      type: String,
      default: '',
    },
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter();
    const step = ref(1);
    const phoneNumber = ref('');
    const phoneError = ref('');
    const flowError = ref('');
    const isSearching = ref(false);
    const isSubmitting = ref(false);
    const searchResults = ref<Customer[]>([]);
    const selectedCustomer = ref<Customer | null>(null);
    const customerStore = useCustomerStore();
    const appointmentStore = useAppointmentStore();
    const authStore = useAuthStore();
    const userRole = computed(() => authStore.user?.role?.name?.toLowerCase());

    const isStaff: ComputedRef<boolean> = computed(() =>
      hasAdminPortalAccess(userRole.value)
    );

    const isAcademyEnquiry = computed(() => props.flowType === 'academy-enquiry');

    const minSuggestionLength = 2;

    const showStaffSuggestions = computed(() =>
      isStaff.value && step.value === 1 && phoneNumber.value.trim().length >= minSuggestionLength
    );

    const validatePhone = (): boolean => {
      const normalized = normalizePhone(phoneNumber.value);
      if (!normalized || normalized.length < (isStaff.value ? 7 : 10)) {
        phoneError.value = isStaff.value
          ? 'Enter at least 7 characters to search, or pick a customer from the list.'
          : 'Please enter a valid 10-digit phone number.';
        return false;
      }
      phoneError.value = '';
      return true;
    };

    const fetchStaffSuggestions = async () => {
      if (!isStaff.value || step.value !== 1) return;

      const query = phoneNumber.value.trim();
      if (query.length < minSuggestionLength) {
        searchResults.value = [];
        return;
      }

      isSearching.value = true;
      flowError.value = '';
      try {
        await customerStore.searchCustomers({ q: query });
        searchResults.value = customerStore.searchResults;
      } catch (error: unknown) {
        flowError.value = getApiErrorMessage(error, 'Could not search customers. Please try again.');
        searchResults.value = [];
      } finally {
        isSearching.value = false;
      }
    };

    const debouncedStaffSearch = debounce(fetchStaffSuggestions, 300);

    const onPhoneInput = () => {
      phoneError.value = '';
      if (isStaff.value && step.value === 1) {
        debouncedStaffSearch();
      }
    };

    const advanceAfterSearch = (results: Customer[]) => {
      searchResults.value = results;
      flowError.value = '';

      if (isAcademyEnquiry.value) {
        if (results.length === 0) {
          step.value = 3;
        } else if (results.length === 1) {
          selectedCustomer.value = results[0];
          submitAcademyEnquiry();
        } else {
          step.value = 2;
        }
        return;
      }

      if (results.length === 0) {
        step.value = 3;
      } else if (results.length === 1) {
        selectedCustomer.value = results[0];
        step.value = 4;
      } else {
        step.value = 2;
      }
    };

    const searchCustomer = async () => {
      if (!validatePhone()) return;

      isSearching.value = true;
      flowError.value = '';
      const normalized = normalizePhone(phoneNumber.value);
      phoneNumber.value = normalized;

      try {
        if (isStaff.value) {
          await customerStore.searchCustomers({ q: normalized });
          advanceAfterSearch(customerStore.searchResults);
        } else {
          await customerStore.searchCustomerByPhone(normalized);
          advanceAfterSearch(customerStore.searchResults);
        }
      } catch (error: unknown) {
        flowError.value = getApiErrorMessage(error, 'Could not look up your phone number. Please try again.');
        searchResults.value = [];
      } finally {
        isSearching.value = false;
      }
    };

    const selectCustomer = (customer: Customer) => {
      selectedCustomer.value = customer;
      phoneNumber.value = customer.phoneNumber;
      searchResults.value = [];
      if (isAcademyEnquiry.value) {
        submitAcademyEnquiry();
        return;
      }
      step.value = 4;
    };

    const proceedToCreateFromSearch = () => {
      selectedCustomer.value = null;
      step.value = 3;
    };

    const proceedToCreate = () => {
      selectedCustomer.value = null;
      step.value = 3;
    };

    const goBackToPhone = () => {
      step.value = 1;
      selectedCustomer.value = null;
      searchResults.value = [];
      flowError.value = '';
    };

    const changeCustomer = () => {
      if (searchResults.value.length > 1) {
        step.value = 2;
      } else {
        goBackToPhone();
      }
    };

    const handleCustomerSave = (newCustomer: Customer) => {
      selectedCustomer.value = newCustomer;
      if (isAcademyEnquiry.value) {
        submitAcademyEnquiry();
        return;
      }
      step.value = 4;
    };

    const submitAcademyEnquiry = async () => {
      if (!selectedCustomer.value?.id) {
        flowError.value = 'Customer not selected. Please go back and try again.';
        return;
      }

      flowError.value = '';
      isSubmitting.value = true;

      try {
        const newAppointment = await appointmentStore.createAcademyEnquiry({
          clientId: selectedCustomer.value.id,
          notes: 'Academy enquiry',
        });
        router.push({
          name: 'BookingSuccess',
          params: { id: newAppointment.id },
          query: { type: 'enquiry', token: newAppointment.viewToken },
          state: { appointment: JSON.parse(JSON.stringify(newAppointment)) },
        });
      } catch (error: unknown) {
        flowError.value = getApiErrorMessage(error, 'Failed to submit your enquiry. Please try again.');
      } finally {
        isSubmitting.value = false;
      }
    };

    const handleAppointmentSubmit = async (appointmentData: any) => {
      flowError.value = '';
      const payload = {
        clientId: appointmentData.clientId || selectedCustomer.value?.id,
        appointmentDate: appointmentData.appointmentDate,
        services: (appointmentData.services || [])
          .filter((s: { serviceId?: string }) => s.serviceId)
          .map((s: { serviceId: string; employeeId?: string; price?: number }) => ({
            serviceId: s.serviceId,
            employeeId: s.employeeId || undefined,
            ...(s.price != null ? { priceOverride: s.price } : {}),
          })),
        notes: appointmentData.notes,
      };

      if (!payload.clientId) {
        flowError.value = 'Customer not selected. Please go back and try again.';
        return;
      }
      if (!payload.services.length) {
        flowError.value = 'Please select at least one service.';
        return;
      }

      try {
        if (isStaff.value) {
          await appointmentStore.createAppointment(payload);
          emit('close');
          router.push('/admin/appointments');
        } else {
          const newAppointment = await appointmentStore.createPublicAppointment(payload);
          router.push({
            name: 'BookingSuccess',
            params: { id: newAppointment.id },
            query: { token: newAppointment.viewToken },
            state: { appointment: JSON.parse(JSON.stringify(newAppointment)) },
          });
        }
      } catch (error: unknown) {
        flowError.value = getApiErrorMessage(error, 'Failed to book appointment. Please try again.');
      }
    };

    onMounted(() => {
      const initial = props.initialPhoneNumber?.trim();
      if (!initial) return;
      phoneNumber.value = normalizePhone(initial);
      // Same path as Continue: lookup → profile form or appointment step.
      void searchCustomer();
    });

    return {
      step,
      phoneNumber,
      phoneError,
      flowError,
      isSearching,
      isSubmitting,
      isAcademyEnquiry,
      searchResults,
      selectedCustomer,
      searchCustomer,
      selectCustomer,
      isStaff,
      showStaffSuggestions,
      minSuggestionLength,
      onPhoneInput,
      proceedToCreate,
      proceedToCreateFromSearch,
      goBackToPhone,
      changeCustomer,
      handleCustomerSave,
      handleAppointmentSubmit,
    };
  },
});
</script>

<style scoped>
.dynamic-appointment-flow {
  max-width: 520px;
  margin: 2rem auto;
  padding: 2rem;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  background-color: #fff;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.form-group input {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  box-sizing: border-box;
}

.btn {
  padding: 10px 20px;
  border-radius: 5px;
  border: none;
  cursor: pointer;
  font-weight: bold;
}

.btn-primary {
  background-color: var(--brand-gold);
  color: var(--brand-black);
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-link {
  background: none;
  border: none;
  color: #2563eb;
  cursor: pointer;
  font-size: 0.9rem;
  padding: 4px 8px;
  text-decoration: underline;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.search-results {
  list-style: none;
  padding: 0;
  margin: 20px 0;
  border: 1px solid #ddd;
  border-radius: 4px;
  max-height: 240px;
  overflow-y: auto;
}

.search-results li {
  padding: 15px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #eee;
}

.search-results li:last-child {
  border-bottom: none;
}

.search-results li:hover {
  background-color: #f7f7f7;
}

.search-results li small {
  display: block;
  color: #6c757d;
  margin-top: 2px;
}

.select-hint {
  color: var(--brand-gold);
  font-size: 0.85rem;
  font-weight: 600;
}

.new-customer-prompt {
  text-align: center;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px dashed #ddd;
}

.customer-label {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 8px;
}

.error-text {
  color: #dc3545;
  font-size: 0.875em;
}

.flow-error {
  margin-top: 1rem;
  text-align: center;
}

.submitting-overlay {
  margin-top: 1rem;
  padding: 1rem;
  text-align: center;
  color: #555;
  font-weight: 600;
}

.suggestions-panel {
  margin-bottom: 1rem;
}

.suggestions-title {
  font-size: 0.85rem;
  font-weight: 600;
  color: #6c757d;
  margin: 0 0 8px;
}

.hint-text {
  color: #6c757d;
  font-size: 0.875em;
}

.no-results {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0 0 12px;
  padding: 12px;
  background: #f9fafb;
  border-radius: 4px;
  text-align: center;
}

.btn-block {
  width: 100%;
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .dynamic-appointment-flow {
    margin: 0 auto;
    padding: 1rem;
    max-width: 100%;
    box-shadow: none;
    border-radius: 0;
  }

  .dynamic-appointment-flow h2 {
    margin: 0 0 0.35rem;
    font-size: 1.25rem;
  }

  .dynamic-appointment-flow > div > p {
    margin: 0 0 0.75rem;
    font-size: 0.9rem;
  }

  .form-group {
    margin-bottom: 0.75rem;
  }

  .form-group label {
    margin-bottom: 0.35rem;
    font-size: 0.9rem;
  }

  .search-results {
    margin: 10px 0;
  }

  .search-results li {
    flex-direction: column;
    align-items: flex-start;
    gap: 2px;
    padding: 10px 12px;
  }

  .suggestions-panel {
    margin-bottom: 0.75rem;
  }

  .new-customer-prompt {
    margin-top: 12px;
    padding-top: 12px;
  }

  .customer-label {
    flex-wrap: wrap;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
  }

  .btn-primary,
  .btn-secondary {
    width: 100%;
    min-height: 40px;
    padding: 8px 16px;
    margin-top: 8px;
  }

  .btn-block {
    margin-bottom: 0.5rem;
  }

  .btn-link {
    width: auto;
    min-height: auto;
    margin-top: 4px;
    padding: 4px 0;
  }

  /* Tighten embedded CustomerForm / AppointmentForm inside the flow */
  .dynamic-appointment-flow :deep(.services-section) {
    margin-top: 10px;
    padding-top: 10px;
  }

  .dynamic-appointment-flow :deep(.service-item) {
    margin-bottom: 8px;
    padding: 10px;
  }

  .dynamic-appointment-flow :deep(.form-footer) {
    margin-top: 10px;
    gap: 8px;
  }
}
</style>
